import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, MapPin, Calendar, Users, HandCoins, FileText, Film, Image as ImageIcon, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { CATEGORY_LABELS, resolveMediaUrl } from "@/pages/ImpactHub";

function toYouTubeEmbed(url) {
    if (!url) return null;
    const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
    if (m) return `https://www.youtube.com/embed/${m[1]}`;
    const v = url.match(/vimeo\.com\/(\d+)/);
    if (v) return `https://player.vimeo.com/video/${v[1]}`;
    return url;
}

function isNativeVideo(url) {
    return /\.(mp4|webm|ogg)(\?|$)/i.test(url || "");
}

export default function ImpactProject() {
    const { slug } = useParams();
    const [p, setP] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [lightbox, setLightbox] = useState(null);

    useEffect(() => {
        api.get(`/public/projects/${slug}`).then(({ data }) => setP(data)).catch(() => setNotFound(true));
    }, [slug]);

    if (notFound) {
        return (
            <section className="gsf-section" data-testid="project-error-state">
                <div className="gsf-container max-w-2xl">
                    <div className="gsf-card p-10 text-center">
                        <div className="gsf-eyebrow">We couldn't find that project</div>
                        <h1 className="mt-3 font-serif text-3xl text-[color:var(--gsf-green-700)] font-bold" data-testid="project-not-found-heading">
                            The story you're looking for isn't available yet.
                        </h1>
                        <p className="mt-4 text-[color:var(--gsf-body)] leading-relaxed">
                            This project may be unpublished, renamed, or still being verified by our team. Every
                            success story is reviewed before it goes live.
                        </p>
                        <div className="mt-8 flex flex-wrap justify-center gap-3">
                            <Link to="/impact-hub" className="gsf-btn-primary text-sm" data-testid="project-back-to-hub">
                                <ArrowLeft size={14} /> Back to Impact Hub
                            </Link>
                            <Link to="/" className="gsf-btn-outline text-sm" data-testid="project-return-home">Return Home</Link>
                            <Link to="/contact" className="gsf-btn-ghost text-sm" data-testid="project-contact-us">Contact us</Link>
                        </div>
                    </div>
                </div>
            </section>
        );
    }
    if (!p) return <div className="gsf-section text-center text-gray-500" data-testid="project-loading">Loading…</div>;

    const hero = p.images?.[0];

    return (
        <div data-testid="impact-project">
            <section className="relative bg-[color:var(--gsf-green-900)] text-white overflow-hidden" data-testid="project-hero">
                {hero && (
                    <div className="absolute inset-0">
                        <img src={resolveMediaUrl(hero)} alt={p.title} className="w-full h-full object-cover opacity-40" />
                        <div className="absolute inset-0 bg-gradient-to-r from-[#0a1f14]/95 via-[#0f2b1d]/70 to-transparent" />
                    </div>
                )}
                <div className="relative gsf-container py-20 sm:py-28">
                    <Link to="/impact-hub" className="text-xs text-white/70 hover:text-white flex items-center gap-1 mb-4" data-testid="project-back-link">
                        <ArrowLeft size={12} /> Back to Impact Hub
                    </Link>
                    <div className="gsf-eyebrow text-[color:var(--gsf-gold-500)]">{CATEGORY_LABELS[p.category] || p.category || "Project"}</div>
                    <h1 className="mt-3 font-serif font-bold text-4xl sm:text-5xl max-w-4xl leading-tight">{p.title}</h1>
                    {p.summary && <p className="mt-4 text-white/85 max-w-3xl leading-relaxed">{p.summary}</p>}
                    <div className="mt-5 flex flex-wrap gap-3 text-xs">
                        {p.verified && <span className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-green-500/20 border border-green-400/40" data-testid="verified-badge"><CheckCircle2 size={12} /> Verified impact</span>}
                        {p.status && <span className="px-3 py-1.5 rounded-full bg-white/10 border border-white/20 capitalize">{p.status}</span>}
                        {p.completion_date && <span className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/10 border border-white/20"><Calendar size={12} /> {p.completion_date}</span>}
                        {p.location_name && <span className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/10 border border-white/20"><MapPin size={12} /> {p.location_name}</span>}
                    </div>
                </div>
            </section>

            <section className="gsf-section" data-testid="project-narrative">
                <div className="gsf-container grid md:grid-cols-3 gap-10">
                    <div className="md:col-span-2 space-y-8">
                        {p.background && <NarrativeBlock label="Background" body={p.background} />}
                        {p.challenge && <NarrativeBlock label="Challenge" body={p.challenge} />}
                        {p.intervention && <NarrativeBlock label="Intervention" body={p.intervention} />}
                        {p.outcome && <NarrativeBlock label="Outcome" body={p.outcome} />}
                    </div>

                    <aside className="md:col-span-1 space-y-4">
                        <div className="gsf-card p-6 space-y-3 text-sm">
                            {p.beneficiaries_count > 0 && <StatRow icon={Users} label="Beneficiaries" value={p.beneficiaries_count.toLocaleString()} />}
                            {p.funding_target > 0 && <StatRow icon={HandCoins} label="Funding" value={`${p.funding_current.toLocaleString()} / ${p.funding_target.toLocaleString()} ${p.currency || "USD"}`} />}
                            {(p.funding_partners || []).length > 0 && (
                                <div>
                                    <div className="text-xs text-gray-500 uppercase tracking-widest">Funding partners</div>
                                    <ul className="mt-1 text-[color:var(--gsf-charcoal)]">{p.funding_partners.map((fp) => <li key={fp}>{fp}</li>)}</ul>
                                </div>
                            )}
                            {(p.tags || []).length > 0 && (
                                <div className="flex flex-wrap gap-1.5 pt-2">
                                    {p.tags.map((t) => <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-[color:var(--gsf-soft-green)] text-[color:var(--gsf-green-700)]">{t}</span>)}
                                </div>
                            )}
                        </div>
                        <Link to="/donate" className="gsf-btn-gold w-full justify-center" data-testid="project-sponsor-cta">Sponsor this programme</Link>
                        {p.slug === "zimshine-community-enterprise" && (
                            <Link to="/enterprise-portfolio" className="gsf-btn-outline w-full justify-center text-sm" data-testid="project-portfolio-cta">
                                View Enterprise Portfolio
                            </Link>
                        )}
                    </aside>
                </div>
            </section>

            {((p.images || []).length > 0 || (p.videos || []).length > 0) && (
                <section className="bg-[color:var(--gsf-soft-green)] gsf-section" data-testid="project-gallery">
                    <div className="gsf-container">
                        <div className="gsf-eyebrow">Photo gallery</div>
                        <h2 className="mt-2 font-serif text-2xl text-[color:var(--gsf-green-700)] font-bold">Evidence in pictures</h2>
                        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                            {(p.images || []).map((img, i) => (
                                <button key={`img-${i}`} onClick={() => setLightbox(resolveMediaUrl(img))} className="aspect-square overflow-hidden rounded-xl group" data-testid={`gallery-${i}`}>
                                    <img src={resolveMediaUrl(img)} alt={`${p.title} — ${i + 1}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                                </button>
                            ))}
                            {(p.videos || []).filter((v) => isNativeVideo(v.url)).map((v, i) => (
                                <figure key={`vid-${i}`} className="aspect-square rounded-xl overflow-hidden bg-black relative group" data-testid={`gallery-video-${i}`}>
                                    <video
                                        src={resolveMediaUrl(v.url)}
                                        controls
                                        preload="metadata"
                                        className="w-full h-full object-cover"
                                        data-testid={`gallery-native-video-${i}`}
                                    />
                                </figure>
                            ))}
                        </div>
                        {(p.videos || []).some((v) => v.caption) && (
                            <p className="mt-4 text-xs italic text-[color:var(--gsf-body)] max-w-3xl" data-testid="gallery-video-caption">
                                {p.videos.find((v) => v.caption)?.caption}
                            </p>
                        )}
                    </div>
                </section>
            )}

            {(p.videos || []).length > 0 && (
                <section className="gsf-section" data-testid="project-videos">
                    <div className="gsf-container">
                        <div className="gsf-eyebrow">Video documentation</div>
                        <h2 className="mt-2 font-serif text-2xl text-[color:var(--gsf-green-700)] font-bold">Community stories on video</h2>
                        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                            {p.videos.map((v, i) => {
                                const url = resolveMediaUrl(v.url);
                                const native = isNativeVideo(v.url);
                                return (
                                    <div key={i} className="gsf-card overflow-hidden" data-testid={`video-${i}`}>
                                        <div className="aspect-video bg-black">
                                            {native ? (
                                                <video src={url} controls preload="metadata" className="w-full h-full" data-testid={`native-project-video-${i}`} />
                                            ) : (
                                                <iframe src={toYouTubeEmbed(v.url)} title={v.title || `Video ${i + 1}`} className="w-full h-full" allowFullScreen loading="lazy" referrerPolicy="strict-origin-when-cross-origin" />
                                            )}
                                        </div>
                                        {v.title && <div className="p-4 text-sm font-medium">{v.title}</div>}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {(p.testimonials || []).length > 0 && (
                <section className="bg-[color:var(--gsf-soft-green)] gsf-section" data-testid="project-testimonials">
                    <div className="gsf-container">
                        <div className="gsf-eyebrow">Community voices</div>
                        <h2 className="mt-2 font-serif text-2xl text-[color:var(--gsf-green-700)] font-bold">In their own words</h2>
                        <div className="mt-6 grid md:grid-cols-2 gap-6">
                            {p.testimonials.map((t, i) => (
                                <blockquote key={i} className="gsf-card p-6" data-testid={`testimonial-${i}`}>
                                    <p className="italic text-[color:var(--gsf-body)] leading-relaxed">“{t.quote}”</p>
                                    <footer className="mt-4 text-sm font-medium text-[color:var(--gsf-green-700)]">{t.name}{t.role ? `, ${t.role}` : ""}</footer>
                                </blockquote>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {(p.documents || []).length > 0 && (
                <section className="gsf-section" data-testid="project-documents">
                    <div className="gsf-container">
                        <div className="gsf-eyebrow">Documents</div>
                        <h2 className="mt-2 font-serif text-2xl text-[color:var(--gsf-green-700)] font-bold">Downloads and reports</h2>
                        <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                            {p.documents.map((d, i) => (
                                <li key={i}>
                                    <a href={resolveMediaUrl(d.url)} target="_blank" rel="noreferrer" className="gsf-card p-4 flex items-center gap-3 hover:border-[color:var(--gsf-green-600)]" data-testid={`doc-${i}`}>
                                        <FileText size={20} className="text-[color:var(--gsf-green-700)]" />
                                        <span className="text-sm font-medium">{d.name || d.url}</span>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            )}

            {lightbox && (
                <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6" onClick={() => setLightbox(null)} data-testid="lightbox">
                    <img src={lightbox} alt="Enlarged view" className="max-w-full max-h-full object-contain" />
                </div>
            )}
        </div>
    );
}

function NarrativeBlock({ label, body }) {
    return (
        <div>
            <div className="gsf-eyebrow">{label}</div>
            <div className="mt-2 text-[color:var(--gsf-body)] leading-relaxed whitespace-pre-line">{body}</div>
        </div>
    );
}

function StatRow({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-3">
            <Icon size={16} className="text-[color:var(--gsf-green-700)]" />
            <div>
                <div className="text-[10px] text-gray-500 uppercase tracking-widest">{label}</div>
                <div className="font-medium">{value}</div>
            </div>
        </div>
    );
}
